// DocumentViewer.vue
<script lang="ts" setup>
import { onBeforeMount, reactive } from 'vue';
import Icon from '../../components/Icon.vue';
import { IconTypes, IconAnimation, TextDisplayStyle } from '../../Enums/UiEnums';
import { popAlert } from '../../lib/Notification';
import { ChoiceApi } from '../../LibMain';
import { initPinia } from '../../lib/PiniaMgr';
import { useSurescriptsRecordLocatorStore } from '../../lib/Stores/SurescriptsRecordLocatorStore';
import MessageText from '../../components/Messages/MessageText.vue';
import { IDocumentListItem, isCCDDocument, documentTitle } from './Models/IDocumentListItem';
import { DocumentRetrieveStatus } from './Models/DocumentRetrieveStatus';

const surescriptsRecordLocatorStore = useSurescriptsRecordLocatorStore(initPinia());

    const props = defineProps<{
        documentToView: IDocumentListItem,
        patientId: number
    }>();

    const emits = defineEmits<{
        (e: "documentLoaded"): void        
        (e: "errorLoadingDocument"): void        
    }>();

    const compData = reactive<{
        loadingDocumentToView?:boolean,
        documentToViewBlobUrl?: string
    }>({});

    onBeforeMount(async ()=> onViewDocument());

    async function onViewDocument()
    {
        compData.documentToViewBlobUrl = undefined;
        compData.loadingDocumentToView = true;

        // Update status if we've not gotten the document yet
        switch(props.documentToView.retrieveStatus)
        {
            case DocumentRetrieveStatus.Available:
            case DocumentRetrieveStatus.Error:
            case undefined:
            props.documentToView.retrieveStatus = DocumentRetrieveStatus.Retrieving;
            break;
        }

        compData.documentToViewBlobUrl = await getDocumentToView();
    }

    async function getDocumentToView()
    {
        if (!props.documentToView?.url || !props.documentToView?.contentType)
            return;

        
        if (isCCDDocument(props.documentToView))
        {            
            return ChoiceApi.CCD.surescriptsCcdViewerUrl(props.documentToView.url, props.patientId);
        }
        else
        {
            const resBlob = await surescriptsRecordLocatorStore.getDocumentResourceResponseBlob(props.documentToView.url, props.patientId);
            if (!resBlob)
            {
                emits("errorLoadingDocument");
                return;
            }
            
            // Create a temporary URL pointing to the Blob
            const url = URL.createObjectURL(resBlob);        
            return url;                
        }
    };

    function documentLoaded(_evt: any)
    {
        if (compData.documentToViewBlobUrl)
        {
            compData.loadingDocumentToView = false;

            if (isCCDDocument(props.documentToView))
            {
                // The load event occurs even if there is an error, so make sure we get HTML for the CCD
                const contentType = _evt.target.contentDocument.contentType;
                if ("text/html" != contentType)
                {
                    emits("errorLoadingDocument");
                    return;
                }
            }

            // Indicate if the document has been retrieved
            if (DocumentRetrieveStatus.Retrieving === props.documentToView?.retrieveStatus)
            {
                emits("documentLoaded");
            }
        }
        else
        {
            emits("documentLoaded");            
        }
    }

    // The error handler isn't always called.  There is extra logic in the load method handler
    function errorLoadingDocument(_err: any)
    {
        emits("errorLoadingDocument");
    }
</script>

<template>
    <template v-if="compData.loadingDocumentToView">
        <Icon :icon="IconTypes.SpinnerDots" :animation="IconAnimation.spin" />
        Loading document.
        <div class="ms-3">
            <MessageText :displayStyle="TextDisplayStyle.Muted">Please be patient.  It can take up to 5 minutes to fetch a document from Surescripts.</MessageText>
        </div>
    </template>
    <iframe v-show="!compData.loadingDocumentToView"
            :src="compData.documentToViewBlobUrl" 
            @load="documentLoaded"
            @error="errorLoadingDocument"
            :title="documentTitle(props.documentToView)" 
            style="height: 80vh; width: 90vw">
    </iframe>    
</template>